<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
    <div class="bg-white rounded-3xl max-w-3xl w-full p-6 shadow-2xl relative overflow-hidden animate-fade-in my-8 max-h-[90vh] flex flex-col">
      <div class="flex justify-between items-center mb-4 pb-3 border-b border-gray-100 shrink-0">
        <div>
          <div class="flex items-center gap-2">
            <h2 class="text-xl font-bold text-gray-900">Registro Masivo de Turnos</h2>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800">En Lote</span>
          </div>
          <p class="text-xs text-gray-500">Registra el turno del día para varios o todos tus empleados con 1 clic.</p>
        </div>
        <button 
          type="button"
          class="text-gray-400 hover:text-gray-600 text-lg p-1 cursor-pointer" 
          @click="$emit('close')"
        >
          <i class="fa fa-times" />
        </button>
      </div>

      <form id="bulkShiftFormElement" class="flex-1 overflow-y-auto pr-1 space-y-4" @submit.prevent="handleSubmit">
        <!-- Parámetros Generales -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-200">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Fecha del Turno *</label>
            <input
              v-model="bulkDate"
              type="date"
              required
              class="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-orange-500 focus:border-orange-500 font-semibold"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Tipo de Turno *</label>
            <select
              v-model="bulkShiftType"
              required
              class="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-orange-500 focus:border-orange-500 font-semibold"
            >
              <option value="Jornada Completa">Jornada Completa</option>
              <option value="Turno Diurno">Turno Diurno</option>
              <option value="Turno Nocturno">Turno Nocturno</option>
              <option value="Medio Turno">Medio Turno</option>
              <option value="Destajo / Producción">Destajo / Producción</option>
              <option value="Feriado / Fin de Semana">Feriado / Fin de Semana</option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Evento / Carpeta (Lote)</label>
            <input
              v-model="bulkEventName"
              type="text"
              placeholder="Ej. Concierto, Expo..."
              class="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-orange-500 focus:border-orange-500 font-semibold"
            />
          </div>
        </div>

        <!-- Selección de Empleados y Tarifas -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <label class="text-xs font-bold uppercase tracking-wider text-gray-700">
              Selecciona el Personal a Registrar
            </label>
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="text-xs font-bold text-orange-600 hover:text-orange-700 cursor-pointer"
                @click="selectAll(true)"
              >
                Seleccionar Todos
              </button>
              <span class="text-gray-300">|</span>
              <button
                type="button"
                class="text-xs font-bold text-gray-500 hover:text-gray-700 cursor-pointer"
                @click="selectAll(false)"
              >
                Deseleccionar
              </button>
            </div>
          </div>

          <div class="border border-gray-200 rounded-2xl overflow-hidden divide-y divide-gray-100 max-h-72 overflow-y-auto">
            <div 
              v-for="item in bulkItems" 
              :key="item.employee_id"
              class="p-3 flex items-center justify-between gap-3 hover:bg-gray-50/70 transition-colors"
              :class="item.selected ? 'bg-orange-50/20' : 'opacity-70'"
            >
              <div class="flex items-center gap-3 min-w-0">
                <input
                  v-model="item.selected"
                  type="checkbox"
                  class="w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500 cursor-pointer"
                />
                <div class="truncate">
                  <div class="font-bold text-gray-900 text-sm truncate">{{ item.name }}</div>
                  <div class="text-xs text-gray-500">{{ item.role_or_job || 'Personal' }}</div>
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <div class="w-20">
                  <input
                    v-model.number="item.hours"
                    type="number"
                    step="0.5"
                    min="0"
                    :disabled="!item.selected"
                    placeholder="Horas"
                    class="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-900 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                    @input="onBulkItemHoursChange(item)"
                  />
                </div>
                <div class="w-28">
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <span class="text-xs font-bold text-gray-400 leading-none">$</span>
                    </div>
                    <input
                      v-model.number="item.amount"
                      type="number"
                      step="0.01"
                      min="0"
                      :disabled="!item.selected"
                      placeholder="0.00"
                      class="w-full pl-6 pr-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-900 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Total Summary -->
        <div class="bg-gray-900 text-white p-4 rounded-2xl flex items-center justify-between shadow-md">
          <div>
            <div class="text-xs text-gray-400 uppercase font-bold">Total a Registrar</div>
            <div class="text-sm font-semibold text-gray-200">
              {{ selectedBulkCount }} personas seleccionadas
            </div>
          </div>
          <div class="text-right">
            <div class="text-xs text-gray-400 uppercase font-bold">Monto Proyectado</div>
            <div class="text-2xl font-bold text-amber-400">
              {{ formatUSD(selectedBulkTotalAmount) }}
            </div>
          </div>
        </div>
      </form>

      <!-- Sticky Footer -->
      <div class="px-6 py-3.5 bg-gray-50 border-t border-gray-100 shrink-0 flex items-center justify-end gap-3 rounded-b-3xl">
        <button
          type="button"
          class="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-50 cursor-pointer"
          @click="$emit('close')"
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="bulkShiftFormElement"
          :disabled="submitting || selectedBulkCount === 0"
          class="px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm shadow-md transition-all disabled:opacity-50 cursor-pointer active:scale-95"
        >
          {{ submitting ? 'Registrando...' : `Registrar ${selectedBulkCount} Turnos` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BulkShiftModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    allActiveEmployees: {
      type: Array,
      default: () => [],
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'submit'],

  data() {
    return {
      bulkDate: new Date().toISOString().split('T')[0],
      bulkShiftType: 'Jornada Completa',
      bulkEventName: '',
      bulkItems: [],
    }
  },

  computed: {
    selectedBulkCount() {
      return this.bulkItems.filter(i => i.selected).length
    },

    selectedBulkTotalAmount() {
      return this.bulkItems
        .filter(i => i.selected)
        .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0)
    },
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.bulkDate = new Date().toISOString().split('T')[0]
        this.bulkShiftType = 'Jornada Completa'
        this.bulkEventName = ''
        this.bulkItems = this.allActiveEmployees.map(emp => ({
          employee_id: emp.id,
          name: emp.name,
          role_or_job: emp.role_or_job,
          amount: emp.rate || 0,
          hours: null,
          notes: '',
          selected: true,
        }))
      }
    },
  },

  methods: {
    selectAll(val) {
      this.bulkItems.forEach(i => {
        i.selected = val
      })
    },

    onBulkItemHoursChange(item) {
      const emp = this.allActiveEmployees.find(e => e.id === item.employee_id)
      if (!emp) return
      const hours = Number(item.hours) || 0
      const rate = Number(emp.rate) || 0
      if (emp.type_salary === 'hourly' || emp.type_salary === 'piecework') {
        if (hours > 0) {
          item.amount = parseFloat((hours * rate).toFixed(2))
        } else {
          item.amount = rate
        }
      }
    },

    handleSubmit() {
      const selected = this.bulkItems.filter(i => i.selected)
      if (selected.length === 0) return

      this.$emit('submit', {
        date: this.bulkDate,
        shift_type: this.bulkShiftType,
        event_name: this.bulkEventName,
        shifts: selected.map(s => ({
          employee_id: s.employee_id,
          amount: s.amount,
          hours: s.hours,
          notes: s.notes,
          event_name: this.bulkEventName,
        })),
      })
    },

    formatUSD(val) {
      const num = parseFloat(val) || 0
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },
}
</script>
